import { useEffect, useMemo, useState } from 'react';
import { assetQuery, getImageUrl, sanityFetch } from '../services/sanity';
import { twoline2satrec, gstime, eciToGeodetic, degreesLong, degreesLat, propagate } from 'satellite.js';
import { craftQuery } from '../adb/crafts';
import { astronautQuery } from '../adb/astronauts';
export const STATION_CRAFT_IMAGE_MARGIN = 70; // 172px in the actual pngs
const craftPreviewMargin = 20;
export var StationVideoEmbedType;
(function (StationVideoEmbedType) {
    StationVideoEmbedType["YoutubeId"] = "youtube-id";
    StationVideoEmbedType["M3U8LivestreamUrl"] = "m3u8-livestream-url";
    StationVideoEmbedType["HTMLEmbed"] = "html-embed";
})(StationVideoEmbedType || (StationVideoEmbedType = {}));
// ---------- FUNCTIONS ----------
export const getStations = async (config) => {
    return sanityFetch(config, `*[
    _type == 'station'
    && !(_id in path("drafts.**"))
  ] ${stationQuery}`);
};
export const getStation = async (config, id) => {
    return sanityFetch(config, `*[
    _type == 'station'
    && _id == '${id}'
  ][0]${stationQuery}`);
};
export const getStationBySlug = async (config, slug) => {
    return sanityFetch(config, `*[
    _type == 'station'
    && slug.current == '${slug}'
  ][0]${stationQuery}`);
};
export const useStationGroundTrack = (station) => {
    const [currentLocation, setCurrentLocation] = useState([0, 0]);
    const [groundTracks, setGroundTracks] = useState([]);
    useEffect(() => {
        if (!station) {
            return;
        }
        let mounted = true;
        const updateGroundTrack = () => {
            const sat = twoline2satrec(station.tleLine1, station.tleLine2);
            const interval = 10;
            const count = 740;
            const _groundTracks = [[]];
            let lastLong = 0;
            let lastLat = 0;
            let _currentLocation;
            for (let i = 0; i < count; i++) {
                const date = new Date();
                date.setSeconds(date.getSeconds() + i * interval);
                const gmst = gstime(date);
                const positionAndVelocity = propagate(sat, date);
                const positionEci = positionAndVelocity.position;
                const positionGd = eciToGeodetic(positionEci, gmst);
                const long = degreesLong(positionGd.longitude);
                const lat = degreesLat(positionGd.latitude);
                if (i > 12) {
                    if (lastLong > 0 && long < 0) {
                        const midLat = lastLat + (lat - lastLat) / 2;
                        _groundTracks[_groundTracks.length - 1].push({
                            coords: [180, midLat],
                            date: Date.parse(date.toISOString())
                        });
                        _groundTracks.push([
                            {
                                coords: [-180, midLat],
                                date: Date.parse(date.toISOString())
                            }
                        ]);
                    }
                    _groundTracks[_groundTracks.length - 1].push({
                        coords: [long, lat],
                        date: Date.parse(date.toISOString())
                    });
                }
                if (i === 0) {
                    _currentLocation = [long, lat];
                }
                lastLong = long;
                lastLat = lat;
            }
            if (mounted && _currentLocation) {
                setGroundTracks(_groundTracks);
                setCurrentLocation(_currentLocation);
            }
        };
        updateGroundTrack();
        const interval = setInterval(updateGroundTrack, 30000);
        return () => {
            clearInterval(interval);
            mounted = false;
        };
    }, [station]);
    const arrows = useMemo(() => {
        let allPoints = [];
        const _arrows = [];
        (groundTracks || []).forEach((gt) => {
            allPoints = allPoints.concat(gt);
        });
        (allPoints || []).forEach((point, index) => {
            if (index % 104 === 0 && index > 10) {
                const p1 = allPoints[index - 1].coords;
                const p2 = point.coords;
                const rotation = -((Math.atan2(p2[1] - p1[1], p2[0] - p1[0]) * 180) / Math.PI) + 90;
                _arrows.push({
                    coords: p2,
                    rotation
                });
            }
        });
        return _arrows;
    }, [groundTracks]);
    return [currentLocation, groundTracks, arrows];
};
export const deriveStationDockedCraftCount = (station) => {
    let count = 0;
    (station?.portGroups || []).forEach((g) => (g.ports || []).forEach((p) => {
        if (p.craft) {
            count += 1;
        }
    }));
    return count;
};
export const deriveStationPortCount = (station) => {
    let count = 0;
    (station?.portGroups || []).forEach((g) => (count += (g.ports || []).length));
    return count;
};
export const deriveStationScaledXValue = (input, stationImageSize, stationLayoutSize) => {
    return (input * stationLayoutSize.width) / stationImageSize.width;
};
export const deriveStationScaledYValue = (input, stationImageSize, stationLayoutSize) => {
    return (input * stationLayoutSize.height) / stationImageSize.height;
};
export const deriveStationScaledRect = (rect, stationImageSize, stationLayoutSize) => {
    return {
        x: deriveStationScaledXValue(rect.x, stationImageSize, stationLayoutSize),
        y: deriveStationScaledYValue(rect.y, stationImageSize, stationLayoutSize),
        width: deriveStationScaledXValue(rect.width, stationImageSize, stationLayoutSize),
        height: deriveStationScaledYValue(rect.height, stationImageSize, stationLayoutSize)
    };
};
export const deriveStationScaledPoint = (point, stationImageSize, stationLayoutSize) => {
    return {
        x: deriveStationScaledXValue(point.x, stationImageSize, stationLayoutSize),
        y: deriveStationScaledYValue(point.y, stationImageSize, stationLayoutSize)
    };
};
export const deriveStationCraftImageUrl = (sanityClient, sanityImage, rotation) => {
    if (!sanityImage) {
        return null;
    }
    return getImageUrl(sanityClient, sanityImage, 1000, undefined, 1, rotation);
};
const deriveStationPortDotPosition = (position, craftFrame, dotMargin, dotDistanceMultiple) => {
    const centerX = craftFrame.x + craftFrame.width / 2;
    const centerY = craftFrame.y + craftFrame.height / 2;
    const topY = craftFrame.y - dotMargin * dotDistanceMultiple;
    const bottomY = craftFrame.y + craftFrame.height + dotMargin * dotDistanceMultiple;
    const leftX = craftFrame.x - dotMargin * dotDistanceMultiple;
    const rightX = craftFrame.x + craftFrame.width + dotMargin * dotDistanceMultiple;
    switch (position) {
        case 'center':
            return { x: centerX, y: centerY };
        case 'top':
            return { x: centerX, y: topY };
        case 'bottom':
            return { x: centerX, y: bottomY };
        case 'left':
            return { x: leftX, y: centerY };
        case 'right':
            return { x: rightX, y: centerY };
        case 'top-left':
            return { x: leftX, y: topY };
        case 'top-right':
            return { x: rightX, y: topY };
        case 'bottom-left':
            return { x: leftX, y: bottomY };
        case 'bottom-right':
            return { x: rightX, y: bottomY };
    }
};
export const deriveStationPortLayouts = (sanityClient, station, showTopView, dotMargin, //15
stationImageSize, stationLayoutSize, dotDistanceMultiple) => {
    const boundaryImages = [];
    const margin = STATION_CRAFT_IMAGE_MARGIN;
    const portLayouts = [];
    (station?.portGroups || []).forEach((portGroup) => {
        (portGroup?.ports || []).forEach((port) => {
            const { _key, craft, craftUniqueName, arrivalDate, overrideArrivalDate, departureDate, overrideDepartureDate, portName, portNumber, orientationTop, orientationSide, locationTopX, locationTopY, locationSideX, locationSideY, labelPrefixedTop, labelPrefixedSide, dotPositionTop, dotPositionSide, dotPositionTopEmpty, dotPositionSideEmpty, craftPreviewDirectionTop, craftPreviewDirectionSide } = port;
            const portCenter = showTopView
                ? { x: locationTopX, y: locationTopY }
                : { x: locationSideX, y: locationSideY };
            const orientation = showTopView ? orientationTop : orientationSide;
            const dotLabelIsPrefixed = showTopView
                ? labelPrefixedTop
                : labelPrefixedSide;
            const isHorizontal = orientation !== 'up' && orientation !== 'down';
            const craftPreviewDirection = showTopView
                ? craftPreviewDirectionTop
                : craftPreviewDirectionSide;
            let craftRotation = 0;
            // If there is no craft docked at thsi port just return the info to layout the port dot
            const craftRectEmpty = {
                ...portCenter,
                width: 0,
                height: 0
            };
            const dotPositionEmpty = deriveStationPortDotPosition(showTopView ? dotPositionTopEmpty : dotPositionSideEmpty, craftRectEmpty, dotMargin, dotDistanceMultiple);
            const emptyPortLayout = {
                _key,
                portName,
                portNumber,
                portGroupName: portGroup.name,
                portGroupColor: portGroup.color,
                dotPosition: deriveStationScaledPoint(dotPositionEmpty, stationImageSize, stationLayoutSize),
                dotLabelIsPrefixed
            };
            if (!craft) {
                portLayouts.push(emptyPortLayout);
                return;
            }
            // Subtract the margin so that we determin the shape and position of the craft itself. This is helpful to pass to the dotMidpointTop and dotMidpointSide funtions on the port so that it can figure out where to position the port's dot
            let craftImageBitmap = craft.sideBitmap || craft.topBitmap || craft.bottomBitmap;
            let craftImageSilhouette = craft.sideSilhouette || craft.topSilhouette || craft.bottomSilhouette;
            let craftImageSilhouetteColored = craft.sideSilhouetteColored ||
                craft.topSilhouetteColored ||
                craft.bottomSilhouetteColored;
            let craftImageHoverBoundary = craft.sideHoverBoundary ||
                craft.topHoverBoundary ||
                craft.bottomHoverBoundary;
            if (orientation === 'top') {
                craftImageBitmap = craft.topBitmap;
                craftImageSilhouette = craft.topSilhouette;
                craftImageSilhouetteColored = craft.topSilhouetteColored;
                craftImageHoverBoundary = craft.topHoverBoundary;
            }
            else if (orientation === 'bottom') {
                craftImageBitmap = craft.bottomBitmap;
                craftImageSilhouette = craft.bottomSilhouette;
                craftImageSilhouetteColored = craft.bottomSilhouetteColored;
                craftImageHoverBoundary = craft.bottomHoverBoundary;
            }
            else {
                craftImageBitmap = craft.sideBitmap;
                craftImageSilhouette = craft.sideSilhouette;
                craftImageSilhouetteColored = craft.sideSilhouetteColored;
                craftImageHoverBoundary = craft.sideHoverBoundary;
            }
            // If we don't have the bitmap there's no point in even rendering the prot
            if (!craftImageBitmap) {
                portLayouts.push(emptyPortLayout);
                return;
            }
            const rawCraftImageSize = {
                width: craftImageBitmap.asset.metadata.dimensions.width / 2,
                height: craftImageBitmap.asset.metadata.dimensions.height / 2
            };
            const craftImageDimensions = isHorizontal
                ? {
                    width: rawCraftImageSize.width,
                    height: rawCraftImageSize.height
                }
                : {
                    width: rawCraftImageSize.height,
                    height: rawCraftImageSize.width
                };
            const craftImageRect = {
                ...craftImageDimensions,
                ...portCenter
            };
            if (orientation === 'left') {
                craftRotation = 0;
                craftImageRect.x -= margin;
                craftImageRect.y -= craftImageRect.height / 2;
            }
            else if (orientation === 'right') {
                craftRotation = 180;
                craftImageRect.x -= craftImageRect.width - margin;
                craftImageRect.y -= craftImageRect.height / 2;
            }
            else if (orientation === 'up') {
                craftRotation = 90;
                craftImageRect.x -= craftImageRect.width / 2;
                craftImageRect.y -= margin;
            }
            else if (orientation === 'down') {
                craftRotation = 270;
                craftImageRect.x -= craftImageRect.width / 2;
                craftImageRect.y -= craftImageRect.height - margin;
            }
            else if (orientation === 'top' || orientation === 'bottom') {
                craftImageRect.x -= craftImageRect.width / 2;
                craftImageRect.y -= craftImageRect.height / 2;
            }
            const craftRect = {
                x: craftImageRect.x + margin,
                y: craftImageRect.y + margin,
                width: craftImageRect.width - margin * 2,
                height: craftImageRect.height - margin * 2
            };
            const craftPreviewRect = {
                x: 0,
                y: 0,
                width: 1000,
                height: 600
            };
            if (craftPreviewDirection === 'top') {
                craftPreviewRect.x =
                    craftRect.x + craftRect.width / 2 - craftPreviewRect.width / 2;
                craftPreviewRect.y =
                    craftRect.y - craftPreviewMargin - craftPreviewRect.height;
            }
            else if (craftPreviewDirection === 'bottom') {
                craftPreviewRect.x =
                    craftRect.x + craftRect.width / 2 - craftPreviewRect.width / 2;
                craftPreviewRect.y =
                    craftRect.y + craftPreviewMargin + craftRect.height;
            }
            else if (craftPreviewDirection === 'left') {
                craftPreviewRect.x =
                    craftRect.x - craftPreviewMargin - craftPreviewRect.width;
                craftPreviewRect.y =
                    craftRect.y + craftRect.height / 2 - craftPreviewRect.height / 2;
            }
            else if (craftPreviewDirection === 'right') {
                craftPreviewRect.x = craftRect.x + craftPreviewMargin + craftRect.width;
                craftPreviewRect.y =
                    craftRect.y + craftRect.height / 2 - craftPreviewRect.height / 2;
            }
            const boundaryImageUrl = deriveStationCraftImageUrl(sanityClient, craftImageHoverBoundary, craftRotation);
            if (boundaryImageUrl) {
                boundaryImages.push(boundaryImageUrl);
            }
            const dotPosition = deriveStationPortDotPosition(showTopView ? dotPositionTop : dotPositionSide, craftRect, dotMargin, dotDistanceMultiple);
            portLayouts.push({
                _key,
                portName,
                portNumber,
                portGroupName: portGroup.name,
                portGroupColor: portGroup.color,
                dotPosition: deriveStationScaledPoint(dotPosition, stationImageSize, stationLayoutSize),
                dotLabelIsPrefixed,
                craft,
                craftUniqueName,
                craftArrivalDate: arrivalDate,
                craftOverrideArrivalDate: overrideArrivalDate,
                craftDepartureDate: departureDate,
                craftOverrideDepartureDate: overrideDepartureDate,
                craftRect,
                craftRotation,
                craftImageRect,
                craftImageBitmap,
                craftImageSilhouette,
                craftImageSilhouetteColored,
                craftImageHoverBoundary,
                craftPreviewRect
            });
        });
    });
    const boundaryImagesToLoad = [...new Set(boundaryImages)];
    return { portLayouts, boundaryImagesToLoad };
};
// ---------- QUERIES ----------
const stationQuery = `{
  _id,
  slug,
  name,
  fullName,
  crew[]{
    _key,
    astronaut->${astronautQuery},
    arrivalDate,
    overrideArrivalDate,
    departureDate,
    overrideDepartureDate
  },
  portGroups[]{
    _key,
    name,
    color,
    ports[]{
      _key,
      craft->${craftQuery},
      craftUniqueName,
      name,
      arrivalDate,
      overrideArrivalDate,
      departureDate,
      overrideDepartureDate,
      portName,
      portNumber,
      orientationTop,
      orientationSide,
      locationTopX,
      locationTopY,
      locationSideX,
      locationSideY,
      labelPrefixedTop,
      labelPrefixedSide,
      dotPositionTop,
      dotPositionSide,
      dotPositionTopEmpty,
      dotPositionSideEmpty,
      craftPreviewDirectionTop,
      craftPreviewDirectionSide
    }
  },
  timetable,
  videoEmbed {
    type,
    value
  },
  tleLine1,
  tleLine2,
  intrinsicBrightness,
  topImage ${assetQuery},
  sideImage ${assetQuery}
}`;
//# sourceMappingURL=stations.js.map